/**
 * CustomRangePicker — the calendar affordance for the global top period bar (#3,
 * 2026-07-07). A compact button that opens a two-date popover; on Apply it emits
 * (from, to) where `to` is EXCLUSIVE (the chosen end day + 1), matching the
 * server's half-open [from, to) window and the DrillExplorer CustomRange. Dates
 * are UTC days (per-account timezone bucketing is queued with the V2 tz work —
 * change them together or windows stop reconciling).
 */

import { useEffect, useRef, useState } from "react";

/** to-EXCLUSIVE ISO → the inclusive last day, for the date input's display. */
function toInclusiveDisplay(toExclusive: string | undefined): string {
  if (!toExclusive) return "";
  const d = new Date(`${toExclusive}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function CustomRangePicker({
  active,
  activeLabel,
  from,
  to,
  onApply,
}: {
  active: boolean;
  activeLabel?: string;
  /** Current custom from (ISO) — seeds the inputs when reopening. */
  from?: string;
  /** Current custom to (ISO, EXCLUSIVE) — seeds the inputs (shown as inclusive). */
  to?: string;
  onApply: (from: string, toExclusive: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(from ?? "");
  const [t, setT] = useState(toInclusiveDisplay(to));
  const wrapRef = useRef<HTMLDivElement>(null);

  // Reseed when the active range changes from elsewhere (e.g. a standard pill clears it).
  useEffect(() => {
    setF(from ?? "");
    setT(toInclusiveDisplay(to));
  }, [from, to]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const invalid = !f || !t || f > t;
  const apply = (): void => {
    if (invalid) return;
    // [from, to): bump the inclusive end day +1 so it's counted.
    const end = new Date(`${t}T00:00:00Z`);
    end.setUTCDate(end.getUTCDate() + 1);
    onApply(f, end.toISOString().slice(0, 10));
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => {
          const next = !o;
          if (next) { setF(from ?? ""); setT(toInclusiveDisplay(to)); } // re-seed from the active range on open
          return next;
        })}
        title="Custom date range"
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          border: "1px solid var(--border)", borderRadius: 8, padding: "5px 11px",
          fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
          background: active ? "color-mix(in srgb, var(--accent) 14%, var(--surface))" : "var(--surface)",
          color: active ? "var(--accent)" : "var(--body)",
          borderColor: active ? "color-mix(in srgb, var(--accent) 45%, var(--border))" : "var(--border)",
        }}
      >
        <span aria-hidden style={{ fontSize: 13 }}>📅</span>
        {active ? (activeLabel ?? "Custom") : "Custom"}
      </button>
      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50,
            background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
            boxShadow: "var(--shadow-pop)", padding: 12, display: "flex", flexDirection: "column", gap: 8, minWidth: 240,
          }}
        >
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--body)" }}>Custom date range</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="date" value={f} max={t || undefined} onChange={(e) => setF(e.target.value)} aria-label="From date" style={dateInput} />
            <span style={{ color: "var(--faint)", fontSize: 12 }}>→</span>
            <input type="date" value={t} min={f || undefined} onChange={(e) => setT(e.target.value)} aria-label="To date" style={dateInput} />
          </div>
          <button
            onClick={apply}
            disabled={invalid}
            style={{
              border: "none", borderRadius: 7, padding: "6px 12px", fontSize: 12.5, fontWeight: 600,
              cursor: invalid ? "not-allowed" : "pointer", opacity: invalid ? 0.45 : 1,
              background: "var(--accent)", color: "#fff", alignSelf: "flex-start",
            }}
          >
            Apply
          </button>
          <div style={{ fontSize: 10.5, color: "var(--faint)" }}>Both dates included. Picking a fixed window clears this.</div>
        </div>
      )}
    </div>
  );
}

const dateInput: React.CSSProperties = {
  border: "1px solid var(--border)", borderRadius: 7, padding: "4px 7px",
  fontSize: 12, background: "var(--surface-2)", color: "var(--text)", width: 128,
};
